import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

/* ── Animation variants ─────────────────────────────────── */
const fadeUp   = { hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25,0.46,0.45,0.94] } } };
const fadeLeft = { hidden: { opacity: 0, x: -32 }, show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.25,0.46,0.45,0.94] } } };
const stagger  = { show: { transition: { staggerChildren: 0.12 } } };

/* ── Blur-in text ───────────────────────────────────────── */
const BlurText = ({ text, className = '', delay = 0 }) => {
  const words = text.split(' ');
  return (
    <span className={className}>
      {words.map((w, i) => (
        <motion.span key={i} className="inline-block mr-[0.25em]"
          initial={{ opacity: 0, filter: 'blur(12px)', y: 16 }}
          animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
          transition={{ duration: 0.7, delay: delay + i * 0.08, ease: [0.25,0.46,0.45,0.94] }}>
          {w}
        </motion.span>
      ))}
    </span>
  );
};

/* ── Animated counter ───────────────────────────────────── */
const Counter = ({ to, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 60, damping: 18 });
  const [display, setDisplay] = useState(0);

  useEffect(() => { if (inView) mv.set(to); }, [inView, mv, to]);
  useEffect(() => spring.on('change', v => setDisplay(Math.round(v))), [spring]);

  return <span ref={ref}>{prefix}{display.toLocaleString('es-ES')}{suffix}</span>;
};

/* ── Spotlight card ─────────────────────────────────────── */
const SpotCard = ({ children, className = '' }) => {
  const ref = useRef(null);
  const handleMouse = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    ref.current.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={handleMouse}
      className={`spotlight-card shimmer-border ${className}`}>
      {children}
    </div>
  );
};

/* ── Magnetic button ────────────────────────────────────── */
const MagneticBtn = ({ children, onClick, className = '' }) => {
  const ref = useRef(null);
  const x = useMotionValue(0), y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18 });
  const sy = useSpring(y, { stiffness: 180, damping: 18 });

  const move = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * 0.35);
    y.set((e.clientY - rect.top  - rect.height / 2) * 0.35);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.button ref={ref} style={{ x: sx, y: sy }}
      onMouseMove={move} onMouseLeave={reset} onClick={onClick}
      whileTap={{ scale: 0.96 }}
      className={className}>
      {children}
    </motion.button>
  );
};

/* ── Section wrapper ────────────────────────────────────── */
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.section ref={ref} id={id} variants={stagger}
      initial="hidden" animate={inView ? 'show' : 'hidden'}
      className={className}>
      {children}
    </motion.section>
  );
};

/* ══════════════════════════════════════════════════════════
   HOUSE SCROLL CANVAS ANIMATION
   Ported from e-home-animation-dark.html — scroll-based canvas
   ══════════════════════════════════════════════════════════ */
const HouseScrollSection = () => {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const pfillRef     = useRef(null);
  const hintRef      = useRef(null);

  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    const pfill     = pfillRef.current;
    const hint      = hintRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');

    // ── PALETTE ───────────────────────────────────────────────────────────────
    const P = {
      bg:        '#0d0d0d',
      wall:      '#1e1e1e',
      wallMid:   '#252525',
      wallLight: '#2e2e2e',
      wallLine:  '#383838',
      roof:      '#f0c040',
      roofDark:  '#c8960a',
      roofLine:  '#a07808',
      door:      '#f0c040',
      doorDark:  '#c8960a',
      window:    '#0a2a20',
      windowFr:  '#2a4a3a',
      chimney:   '#1a1a1a',
      chimneyTop:'#f0c040',
      ground:    '#161616',
      grass:     '#0d1f18',
      text:      '#ffffff',
      textGold:  '#f0c040',
      particle:  '#f0c040',
    };

    // ── UTILS ─────────────────────────────────────────────────────────────────
    const lerp  = (a,b,t) => a+(b-a)*t;
    const clamp = (v,a,b) => Math.max(a,Math.min(b,v));
    const ease  = t => t<.5?2*t*t:-1+(4-2*t)*t;
    const easeO = t => 1-Math.pow(1-t,3);

    let W, H, CX, CY, HS;
    let prog = 0, time = 0, lastNow = performance.now();

    // ── BRICK DEFS ────────────────────────────────────────────────────────────
    function buildBrickDefs() {
      const defs = [];
      const bw = 0.18, bh = 0.076;
      const addRow = (startX, startY, cols, rowCount, baseDelay) => {
        for (let row = 0; row < rowCount; row++) {
          for (let col = 0; col < cols; col++) {
            const offset = (row%2)*0.09;
            defs.push({
              x: startX + col*(bw+0.01) + offset,
              y: startY - row*(bh+0.01),
              w: bw, h: bh,
              color: row%2===0 ? P.wall : P.wallMid,
              group: 'wall',
              delay: baseDelay + row*0.035 + col*0.008
            });
          }
        }
      };
      addRow(-0.85, 0.30, 3, 5, 0.00);
      addRow( 0.27, 0.30, 3, 5, 0.02);
      for (let col=0; col<3; col++) {
        const off = col%2===0?0:0.09;
        defs.push({ x:-0.27+col*(bw+0.01)+off, y:-0.10,           w:bw,h:bh, color:P.wallMid,   group:'wall', delay:0.14+col*0.02 });
        defs.push({ x:-0.27+col*(bw+0.01)+off, y:-0.10-(bh+0.01), w:bw,h:bh, color:P.wallLight, group:'wall', delay:0.17+col*0.02 });
      }
      return defs;
    }

    // ── PIXEL FONT ────────────────────────────────────────────────────────────
    const GLYPHS = {
      'e': [[0,1,1,0],[1,0,0,0],[1,1,1,0],[1,0,0,0],[0,1,1,0]],
      'h': [[1,0,1,0],[1,0,1,0],[1,1,1,0],[1,0,1,0],[1,0,1,0]],
      'o': [[0,1,1,0],[1,0,0,1],[1,0,0,1],[1,0,0,1],[0,1,1,0]],
      'm': [[1,0,1,0],[1,1,1,0],[1,0,1,0],[1,0,1,0],[1,0,1,0]],
    };
    const WORD = ['e','h','o','m','e'];

    function buildLetterBricks() {
      const lbs = [];
      const lw=0.052, lh=0.042, cGap=0.022, rGap=0.008;
      const charW = 4*(lw+rGap) + cGap;
      const totalW = WORD.length * charW;
      const startX = -totalW/2;
      WORD.forEach((ch, ci) => {
        const grid = GLYPHS[ch];
        grid.forEach((row, ri) => {
          row.forEach((on, coli) => {
            if (!on) return;
            lbs.push({
              x: startX + ci*charW + coli*(lw+rGap),
              y: ri*(lh+rGap),
              w: lw, h: lh,
              color: ci===0 ? P.textGold : P.text,
              charIdx: ci,
              delay: ci*0.07 + ri*0.012 + coli*0.004
            });
          });
        });
      });
      return lbs;
    }

    // ── FLYING BRICK ──────────────────────────────────────────────────────────
    class FlyingBrick {
      constructor(def) {
        this.def = def;
        this.reset();
      }
      reset() {
        const angle = Math.random()*Math.PI*2;
        const dist  = 0.5 + Math.random()*0.7;
        this.sx = this.def.x + Math.cos(angle)*dist*1.8;
        this.sy = this.def.y + Math.sin(angle)*dist*1.8 - 0.3;
        this.x = this.sx; this.y = this.sy;
        this.rot   = (Math.random()-0.5)*Math.PI*1.5;
        this.sc    = 0.2 + Math.random()*0.6;
        this.alpha = 0;
      }
      update(lp) {
        const p = clamp(lp,0,1), ep = ease(p);
        this.x     = lerp(this.sx, this.def.x, ep);
        this.y     = lerp(this.sy, this.def.y, ep);
        this.alpha = easeO(p);
        this.rot   = lerp(this.rot, 0, ep);
        this.sc    = lerp(this.sc < 1 ? 0.2 : 1.4, 1, ep);
      }
      draw(scale, ox, oy) {
        const a = clamp(this.alpha,0,1);
        if (a < 0.01) return;
        const d  = this.def;
        const px = ox + this.x*scale;
        const py = oy + this.y*scale;
        const bw = d.w*scale, bh = d.h*scale;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(px+bw/2, py+bh/2);
        ctx.rotate(this.rot);
        ctx.scale(this.sc, this.sc);
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur  = 6;
        ctx.shadowOffsetY = 2;
        ctx.fillStyle = d.color;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-bw/2,-bh/2,bw,bh,2);
        else ctx.rect(-bw/2,-bh/2,bw,bh);
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.globalAlpha = a*0.25;
        ctx.fillStyle = 'rgba(240,192,64,0.3)';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-bw/2,-bh/2,bw,bh*0.35,[2,2,0,0]);
        else ctx.rect(-bw/2,-bh/2,bw,bh*0.35);
        ctx.fill();
        ctx.globalAlpha = a*0.5;
        ctx.strokeStyle = P.wallLine;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-bw/2,-bh/2,bw,bh,2);
        else ctx.rect(-bw/2,-bh/2,bw,bh);
        ctx.stroke();
        ctx.restore();
      }
    }

    // ── SPARK ─────────────────────────────────────────────────────────────────
    class Spark {
      constructor() { this.reset(); }
      reset() {
        this.x    = CX + (Math.random()-0.5)*HS*2.2;
        this.y    = CY + (Math.random()-0.5)*HS*1.8;
        this.vx   = (Math.random()-0.5)*0.5;
        this.vy   = -(0.3+Math.random()*0.5);
        this.r    = 0.8+Math.random()*1.8;
        this.life = Math.random();
        this.max  = 0.5+Math.random()*0.5;
        this.gold = Math.random()>0.4;
      }
      update() {
        this.x += this.vx; this.y += this.vy;
        this.r  *= 0.995;
        this.life += 0.006;
        if (this.life > this.max) this.reset();
      }
      draw(ga) {
        const a = Math.sin((this.life/this.max)*Math.PI)*0.6*ga;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.fillStyle   = this.gold ? P.particle : '#ffffff';
        ctx.shadowColor = this.gold ? '#f0c040' : '#ffffff';
        ctx.shadowBlur  = 4;
        ctx.beginPath();
        ctx.arc(this.x,this.y,this.r,0,Math.PI*2);
        ctx.fill();
        ctx.restore();
      }
    }

    // ── SMOKE ─────────────────────────────────────────────────────────────────
    class Smoke {
      constructor(x,y) {
        this.x=x; this.y=y;
        this.vx=(Math.random()-0.5)*0.4;
        this.vy=-(0.5+Math.random()*0.5);
        this.r=3+Math.random()*3;
        this.life=1; this.decay=0.009+Math.random()*0.005;
      }
      update() {
        this.x+=this.vx; this.y+=this.vy;
        this.r+=0.25; this.life-=this.decay;
        this.vx*=0.98;
      }
      draw() {
        ctx.save();
        ctx.globalAlpha=this.life*0.18;
        ctx.fillStyle='rgba(240,192,64,1)';
        ctx.shadowColor='#f0c040'; ctx.shadowBlur=8;
        ctx.beginPath();
        ctx.arc(this.x,this.y,this.r,0,Math.PI*2);
        ctx.fill();
        ctx.restore();
      }
    }

    // ── STATE ─────────────────────────────────────────────────────────────────
    let brickDefs=[], letterDefs=[];
    let flyWall=[], flyLetters=[];
    let sparks=[], smokes=[];

    function init() {
      HS = Math.min(W,H)*0.30;
      brickDefs  = buildBrickDefs();
      letterDefs = buildLetterBricks();
      flyWall    = brickDefs.map(d  => new FlyingBrick(d));
      flyLetters = letterDefs.map(d => new FlyingBrick(d));
      sparks     = Array.from({length:50}, () => new Spark());
      smokes     = [];
    }

    function resize() {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
      CX = W/2; CY = H/2;
      init();
    }

    // ── DRAW HOUSE ────────────────────────────────────────────────────────────
    function drawHouse(ox, oy) {
      const s = HS;
      const wallA   = clamp((prog-0.08)/0.35, 0,1);
      const roofA   = clamp((prog-0.38)/0.25, 0,1);
      const detailA = clamp((prog-0.52)/0.22, 0,1);

      ctx.save();
      ctx.globalAlpha = wallA*0.35;
      const sg = ctx.createRadialGradient(ox,oy+s*0.42,0, ox,oy+s*0.42,s*0.9);
      sg.addColorStop(0,'rgba(0,0,0,0.8)');
      sg.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=sg;
      ctx.beginPath();
      ctx.ellipse(ox,oy+s*0.42,s*0.9,s*0.07,0,0,Math.PI*2);
      ctx.fill();
      ctx.restore();

      if (wallA>0) {
        ctx.save();
        ctx.globalAlpha=wallA;
        ctx.fillStyle=P.grass;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(ox-s*1.1,oy+s*0.33,s*2.2,s*0.10,[0,0,3,3]);
        else ctx.rect(ox-s*1.1,oy+s*0.33,s*2.2,s*0.10);
        ctx.fill();
        ctx.fillStyle='#0f0f0f';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(ox-s*0.10,oy+s*0.34,s*0.20,s*0.09,2);
        else ctx.rect(ox-s*0.10,oy+s*0.34,s*0.20,s*0.09);
        ctx.fill();
        ctx.restore();
      }

      if (wallA>0) {
        ctx.save();
        ctx.globalAlpha=wallA*0.8;
        ctx.fillStyle=P.wall;
        ctx.beginPath(); ctx.rect(ox-s*0.85,oy-s*0.15,s*0.58,s*0.48); ctx.fill();
        ctx.fillStyle=P.wallMid;
        ctx.beginPath(); ctx.rect(ox+s*0.27,oy-s*0.15,s*0.58,s*0.48); ctx.fill();
        ctx.restore();
      }

      if (detailA>0) {
        ctx.save();
        ctx.globalAlpha=detailA;
        ctx.fillStyle=P.roofDark;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(ox-s*0.13,oy-s*0.01,s*0.26,s*0.33,[5,5,0,0]);
        else ctx.rect(ox-s*0.13,oy-s*0.01,s*0.26,s*0.33);
        ctx.fill();
        ctx.fillStyle='#111';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(ox-s*0.10,oy+s*0.01,s*0.20,s*0.31,[3,3,0,0]);
        else ctx.rect(ox-s*0.10,oy+s*0.01,s*0.20,s*0.31);
        ctx.fill();
        ctx.strokeStyle=P.door;
        ctx.lineWidth=1;
        ctx.globalAlpha=detailA*0.5;
        ctx.beginPath();
        if (ctx.roundRect) { ctx.roundRect(ox-s*0.08,oy+s*0.03,s*0.16,s*0.10,2); ctx.stroke(); ctx.roundRect(ox-s*0.08,oy+s*0.16,s*0.16,s*0.10,2); ctx.stroke(); }
        else { ctx.rect(ox-s*0.08,oy+s*0.03,s*0.16,s*0.10); ctx.stroke(); ctx.rect(ox-s*0.08,oy+s*0.16,s*0.16,s*0.10); ctx.stroke(); }
        ctx.globalAlpha=detailA;
        ctx.fillStyle=P.door;
        ctx.shadowColor='#f0c040'; ctx.shadowBlur=6;
        ctx.beginPath();
        ctx.arc(ox+s*0.07,oy+s*0.20,s*0.018,0,Math.PI*2);
        ctx.fill();
        ctx.restore();
      }

      if (detailA>0) {
        const wins=[
          {x:ox-s*0.70,y:oy-s*0.09},{x:ox-s*0.42,y:oy-s*0.09},
          {x:ox+s*0.32,y:oy-s*0.09},{x:ox+s*0.60,y:oy-s*0.09},
        ];
        wins.forEach(w => {
          ctx.save();
          ctx.globalAlpha=detailA;
          ctx.fillStyle=P.wallLine;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(w.x,w.y,s*0.22,s*0.20,3);
          else ctx.rect(w.x,w.y,s*0.22,s*0.20);
          ctx.fill();
          const wg=ctx.createLinearGradient(w.x,w.y,w.x,w.y+s*0.20);
          wg.addColorStop(0,'#0a2a1f'); wg.addColorStop(1,'#061510');
          ctx.fillStyle=wg;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(w.x+s*0.015,w.y+s*0.015,s*0.19,s*0.17,2);
          else ctx.rect(w.x+s*0.015,w.y+s*0.015,s*0.19,s*0.17);
          ctx.fill();
          ctx.globalAlpha=detailA*0.6;
          ctx.fillStyle='rgba(26,255,154,0.08)';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(w.x+s*0.015,w.y+s*0.015,s*0.19,s*0.17,2);
          else ctx.rect(w.x+s*0.015,w.y+s*0.015,s*0.19,s*0.17);
          ctx.fill();
          ctx.strokeStyle=P.windowFr; ctx.lineWidth=1;
          ctx.beginPath();
          ctx.moveTo(w.x+s*0.11,w.y+s*0.015); ctx.lineTo(w.x+s*0.11,w.y+s*0.185);
          ctx.moveTo(w.x+s*0.015,w.y+s*0.10); ctx.lineTo(w.x+s*0.205,w.y+s*0.10);
          ctx.stroke();
          ctx.globalAlpha=detailA*0.9;
          ctx.fillStyle='#1aff9a';
          ctx.shadowColor='#1aff9a'; ctx.shadowBlur=8;
          ctx.beginPath(); ctx.arc(w.x+s*0.17,w.y+s*0.04,s*0.012,0,Math.PI*2); ctx.fill();
          ctx.restore();
        });
      }

      if (roofA>0) {
        ctx.save();
        ctx.globalAlpha=roofA;
        ctx.fillStyle=P.roof;
        ctx.shadowColor='rgba(240,192,64,0.3)'; ctx.shadowBlur=20;
        ctx.beginPath();
        ctx.moveTo(ox,      oy-s*0.72);
        ctx.lineTo(ox-s*1.00,oy-s*0.15);
        ctx.lineTo(ox+s*1.00,oy-s*0.15);
        ctx.closePath(); ctx.fill();
        ctx.shadowBlur=0;
        ctx.fillStyle=P.roofDark;
        ctx.beginPath();
        ctx.moveTo(ox,      oy-s*0.72);
        ctx.lineTo(ox+s*1.00,oy-s*0.15);
        ctx.lineTo(ox,       oy-s*0.15);
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle=P.roofLine; ctx.lineWidth=1.5; ctx.lineJoin='round';
        ctx.beginPath();
        ctx.moveTo(ox,       oy-s*0.72);
        ctx.lineTo(ox-s*1.00,oy-s*0.15);
        ctx.lineTo(ox+s*1.00,oy-s*0.15);
        ctx.closePath(); ctx.stroke();
        ctx.globalAlpha=roofA*0.2;
        ctx.strokeStyle=P.roofLine; ctx.lineWidth=1;
        for(let i=1;i<5;i++){
          const t=i/5;
          const ty=lerp(oy-s*0.72,oy-s*0.15,t);
          const hw=s*t;
          ctx.beginPath(); ctx.moveTo(ox-hw,ty); ctx.lineTo(ox+hw,ty); ctx.stroke();
        }
        ctx.restore();
      }

      if (roofA>0) {
        ctx.save();
        ctx.globalAlpha=roofA;
        ctx.fillStyle=P.chimney;
        ctx.beginPath(); ctx.rect(ox+s*0.30,oy-s*0.78,s*0.14,s*0.28); ctx.fill();
        ctx.fillStyle=P.chimneyTop;
        ctx.shadowColor='#f0c040'; ctx.shadowBlur=8;
        ctx.beginPath(); ctx.rect(ox+s*0.28,oy-s*0.80,s*0.18,s*0.04); ctx.fill();
        ctx.restore();
      }

      if (detailA>0.5 && prog>0.78 && Math.random()<0.10) {
        smokes.push(new Smoke(CX+HS*0.30+HS*0.37, CY - HS*0.05 - HS*0.82));
      }
    }

    // ── DRAW BG ───────────────────────────────────────────────────────────────
    function drawBackground() {
      ctx.fillStyle=P.bg;
      ctx.fillRect(0,0,W,H);
      const gleft = ctx.createRadialGradient(W*0.15,H*0.6,0,W*0.15,H*0.6,W*0.5);
      gleft.addColorStop(0,'rgba(10,80,50,0.18)');
      gleft.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gleft; ctx.fillRect(0,0,W,H);
      const gright = ctx.createRadialGradient(W*0.85,H*0.4,0,W*0.85,H*0.4,W*0.4);
      gright.addColorStop(0,'rgba(80,50,0,0.12)');
      gright.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gright; ctx.fillRect(0,0,W,H);
    }

    // ── TAGLINE ───────────────────────────────────────────────────────────────
    function drawTagline(ox, oy) {
      if (prog < 0.88) return;
      const a = clamp((prog-0.88)/0.08,0,1);
      const s = HS;
      ctx.save();
      ctx.globalAlpha = a*0.55;
      ctx.fillStyle   = 'rgba(240,192,64,0.7)';
      ctx.font        = `700 ${Math.max(9,s*0.065)}px 'Inter', sans-serif`;
      ctx.textAlign   = 'center';
      ctx.fillText('PLATAFORMA INMOBILIARIA PROFESIONAL', ox, oy+s*1.14);
      ctx.restore();
    }

    // ── MAIN LOOP ─────────────────────────────────────────────────────────────
    let rafId;
    function loop(now) {
      rafId = requestAnimationFrame(loop);
      const dt = Math.min((now-lastNow)/1000,0.05);
      lastNow=now; time+=dt;

      // Progress from container scroll position
      const rect  = container.getBoundingClientRect();
      const total = container.offsetHeight - window.innerHeight;
      prog = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;

      if (pfill) pfill.style.width = (prog*100)+'%';
      if (hint)  hint.style.opacity = prog < 0.04 ? '1' : '0';

      drawBackground();

      const ox=CX, oy=CY - HS*0.05;

      const sa = clamp(prog/0.45,0,1)*clamp((1-prog)/0.25,0,1);
      sparks.forEach(s => { s.update(); s.draw(sa*0.7); });

      drawHouse(ox, oy);

      smokes = smokes.filter(s=>s.life>0);
      smokes.forEach(s=>{s.update();s.draw();});

      const wallProg = clamp((prog-0.05)/0.45, 0,1);
      flyWall.forEach(fb=>{
        const lp = clamp((wallProg-fb.def.delay)/0.22,0,1);
        fb.update(lp);
        if(fb.alpha>0.01) fb.draw(HS, ox, oy);
      });

      const lProg = clamp((prog-0.63)/0.28, 0,1);
      const lScale = HS * 1.65;
      const lOY    = oy + HS * 0.60;
      flyLetters.forEach(fl=>{
        const lp = clamp((lProg-fl.def.delay)/0.18,0,1);
        fl.update(lp);
        if(fl.alpha>0.01) fl.draw(lScale, CX, lOY);
      });

      drawTagline(ox, oy);
    }

    window.addEventListener('resize', resize);
    resize();
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <style>{`@keyframes ehome-blink{0%,100%{opacity:0.3}50%{opacity:1}}`}</style>
      <div ref={containerRef} style={{ height: '500vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh', width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', background: '#0d0d0d'
        }}>
          <canvas ref={canvasRef} style={{
            display: 'block', position: 'absolute', top: 0, left: 0,
            pointerEvents: 'none'
          }} />
          {/* HUD */}
          <div style={{
            position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            zIndex: 10, pointerEvents: 'none'
          }}>
            <div ref={hintRef} style={{
              fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700,
              color: 'rgba(240,192,64,0.5)', letterSpacing: 4, textTransform: 'uppercase',
              animation: 'ehome-blink 2s ease-in-out infinite'
            }}>
              ↓ scroll to build ↓
            </div>
            <div style={{
              width: 160, height: 1, background: 'rgba(240,192,64,0.12)',
              borderRadius: 1, overflow: 'hidden'
            }}>
              <div ref={pfillRef} style={{
                height: '100%', width: '0%',
                background: 'linear-gradient(90deg, #c8960a, #f0c040)',
                borderRadius: 1, boxShadow: '0 0 6px #f0c040',
                transition: 'width 0.06s linear'
              }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── FEATURES DATA ───────────────────────────────────────── */
const FEATURES = [
  { icon: '🏢', title: 'CRM Profesional', desc: 'Gestiona vendedores, compradores y propiedades desde un panel unificado. Pipeline visual, seguimiento automático y historial completo.', color: '#c9a84c' },
  { icon: '🤖', title: 'Análisis con IA', desc: 'Valoración automática de propiedades, perfil inversor personalizado y recomendaciones de mercado en tiempo real con Gemini AI.', color: '#00d4c8' },
  { icon: '📊', title: 'Estadísticas de equipo', desc: 'Dashboards con anillos de progreso, objetivos por agente, tasa de cierre y rendimiento mensual. Todo en un vistazo.', color: '#7c3aed' },
  { icon: '⚡', title: 'Automatizaciones', desc: '35+ eventos configurables y 9 tipos de acción. Notificaciones, emails, tareas automáticas y mensajes de equipo sin esfuerzo.', color: '#f59e0b' },
  { icon: '🏠', title: 'Portal de propiedades', desc: 'Publica y gestiona inmuebles con análisis de mercado integrado, mapa interactivo, rentabilidad y comparables automáticos.', color: '#10b981' },
  { icon: '👥', title: 'Organizaciones', desc: 'Crea equipos, asigna roles, invita agentes y colabora en tiempo real. Chat de equipo y organigrama visual incluidos.', color: '#ec4899' },
];

const PLANS = [
  { name: 'Agente', price: 'Gratis', sub: 'para siempre', features: ['Hasta 2 propiedades', 'Panel básico', 'Perfil público', 'Soporte por email'], cta: 'Crear cuenta', highlight: false, href: null },
  { name: 'Avanzado', price: '€4,99', sub: 'al mes', features: ['Propiedades ilimitadas', 'CRM básico', 'Análisis IA', 'Comunidad inversora', 'Base de conocimiento'], cta: 'Empezar ahora', highlight: true, href: null },
  { name: 'Profesional', price: '€24,99', sub: 'al mes', features: ['Todo lo anterior', 'CRM ilimitado', 'Organizaciones y equipos', 'Automatizaciones', 'Estadísticas avanzadas', 'Soporte prioritario'], cta: 'Contactar ventas', highlight: false, href: 'https://iaconsultora.com' },
];

const TESTIMONIALS = [
  { name: 'Carlos Moreno', role: 'Director, Inmobiliaria Moreno', quote: 'Pasamos de 3 herramientas distintas a solo E-HOME. El CRM de equipo nos ahorra 2 horas diarias.', avatar: 'CM' },
  { name: 'Lucía Ferrández', role: 'Agente independiente, Valencia', quote: 'El análisis de mercado con IA me da argumentos reales ante los clientes. Ha subido mis cierres un 40%.', avatar: 'LF' },
  { name: 'Grupo Reyes Inmuebles', role: 'Franquicia · 12 agentes', quote: 'Las automatizaciones y los objetivos por agente cambiaron nuestra forma de gestionar el equipo.', avatar: 'GR' },
];

/* ── Nav ────────────────────────────────────────────────── */
const Nav = ({ onLogin }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <motion.nav initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-4 flex items-center justify-between transition-all duration-500 ${
        scrolled ? 'bg-[#050508]/90 backdrop-blur-xl border-b border-white/5' : ''
      }`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#050508', border: '1.5px solid rgba(201,168,76,0.3)' }}>
          <svg viewBox="0 0 40 40" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="20,5 4,18 36,18" fill="#f0c040"/>
            <rect x="26" y="8" width="4" height="8" fill="#f0c040"/>
            <rect x="7" y="18" width="26" height="17" fill="#f0c040"/>
            <rect x="15" y="25" width="10" height="10" fill="#050508" rx="1"/>
            <circle cx="23" cy="30" r="1" fill="#c9a84c"/>
            <rect x="9" y="20" width="7" height="6" fill="#050508" rx="1"/>
            <rect x="24" y="20" width="7" height="6" fill="#050508" rx="1"/>
          </svg>
        </div>
        <span className="font-display font-bold text-white text-lg tracking-tight">E-<span className="text-[#c9a84c]">HOME</span></span>
      </div>
      <div className="hidden md:flex items-center gap-8 text-sm font-body text-[#7a7870]">
        {[
          { label: 'Producto', id: 'features' },
          { label: 'Precios',  id: 'pricing' },
          { label: 'Clientes', id: 'testimonials' },
          { label: 'Contacto', id: 'contact' },
        ].map(item => (
          <button key={item.label}
            onClick={() => { const el = document.getElementById(item.id); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="hover:text-white transition-colors duration-200">{item.label}</button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onLogin}
          className="text-sm font-body font-medium text-[#7a7870] hover:text-white transition-colors duration-200 hidden md:block">
          Iniciar sesión
        </button>
        <button onClick={onLogin}
          className="text-sm font-body font-semibold bg-[#c9a84c] hover:bg-[#e8c870] text-black px-4 py-2 rounded-lg transition-all duration-200 hover:shadow-[0_0_24px_rgba(201,168,76,0.4)]">
          Empezar gratis
        </button>
      </div>
    </motion.nav>
  );
};

/* ═══════════════════════════════════════════════════════════ */
export const LandingPublic = () => {
  const navigate = useNavigate();
  const toLogin = () => navigate('/login');

  return (
    <div className="grain bg-[#050508] text-[#e8e6e0] font-body" style={{ overflowX: 'clip' }}>
      <Nav onLogin={toLogin} />

      {/* ── HERO: CANVAS SCROLL ANIMATION (500vh) ───────── */}
      <HouseScrollSection />

      {/* ── HERO CONTENT ─────────────────────────────────── */}
      <div className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
        <div className="aurora-bg">
          <div className="aurora-blob" />
          <div className="aurora-blob" />
          <div className="aurora-blob" />
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-white/5 border border-[#c9a84c]/30 rounded-full px-4 py-1.5 text-xs font-semibold text-[#c9a84c] mb-8 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse" />
          Plataforma inmobiliaria profesional — ehomesp.com
        </motion.div>

        <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl mb-6 relative z-10">
          <BlurText text="La plataforma que" className="block text-white" delay={0.1} />
          <BlurText text="las inmobiliarias" className="block" delay={0.4} />
          <motion.span className="block bg-gradient-to-r from-[#c9a84c] via-[#e8c870] to-[#c9a84c] bg-clip-text text-transparent"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}>
            necesitaban.
          </motion.span>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="text-[#7a7870] text-lg md:text-xl max-w-2xl mb-10 leading-relaxed font-light">
          CRM profesional, análisis de mercado con IA y gestión completa de propiedades.
          Todo en un solo lugar, diseñado para agencias e inmobiliarias españolas.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center gap-4">
          <MagneticBtn onClick={toLogin}
            className="group relative bg-[#c9a84c] hover:bg-[#e8c870] text-black font-display font-bold text-base px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-[0_0_40px_rgba(201,168,76,0.5)] hover:-translate-y-0.5">
            <span className="relative z-10">Empezar gratis</span>
          </MagneticBtn>
          <button onClick={toLogin}
            className="font-body text-[#7a7870] hover:text-white text-sm flex items-center gap-2 transition-colors group">
            Ver demo en vivo
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.7 }}
          className="flex flex-wrap justify-center gap-10 mt-20 relative z-10">
          {[
            { label: 'Agentes activos',     val: 500,  suffix: '+' },
            { label: 'Propiedades gestión', val: 12000, suffix: '+' },
            { label: 'Satisfacción',        val: 98,   suffix: '%' },
            { label: 'Tiempo ahorrado/día', val: 2,    suffix: 'h' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <div className="font-display font-black text-4xl text-white">
                <Counter to={s.val} suffix={s.suffix} />
              </div>
              <div className="text-[#7a7870] text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── FEATURES ─────────────────────────────────────── */}
      <Section id="features" className="px-6 md:px-12 py-24 max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.2em] mb-4">Plataforma completa</p>
          <h2 className="font-display font-black text-4xl md:text-5xl text-white leading-tight">
            Todo lo que necesita<br className="hidden md:block" /> tu agencia
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div key={i} variants={fadeUp}>
              <SpotCard className="group bg-[#0d0d14] border border-white/7 rounded-2xl p-6 h-full hover:border-white/15 transition-colors duration-300">
                <div className="text-3xl mb-4">{f.icon}</div>
                <div className="w-8 h-0.5 mb-4 rounded-full" style={{ background: f.color }} />
                <h3 className="font-display font-bold text-white text-lg mb-2">{f.title}</h3>
                <p className="text-[#7a7870] text-sm leading-relaxed">{f.desc}</p>
              </SpotCard>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── DEMO VISUAL ──────────────────────────────────── */}
      <Section className="px-6 md:px-12 py-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div variants={fadeLeft}>
            <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.2em] mb-4">CRM Inteligente</p>
            <h2 className="font-display font-black text-4xl md:text-5xl text-white leading-tight mb-6">
              Tu equipo,<br />siempre alineado
            </h2>
            <p className="text-[#7a7870] text-base leading-relaxed mb-8">
              Asigna contactos, fija objetivos por agente y sigue el progreso en tiempo real.
              Con automatizaciones que disparan emails, notificaciones y tareas sin intervención manual.
            </p>
            <div className="space-y-4">
              {[
                'Objetivos por agente con anillos de progreso',
                '35+ eventos automáticos configurables',
                'Chat de equipo y organigrama visual',
                'Estadísticas filtradas por período',
              ].map((item, i) => (
                <motion.div key={i} variants={fadeLeft}
                  className="flex items-center gap-3 text-sm text-[#e8e6e0]">
                  <div className="w-5 h-5 rounded-full bg-[#c9a84c]/20 border border-[#c9a84c]/50 flex items-center justify-center flex-shrink-0">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {item}
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="relative">
            <div className="relative bg-[#0d0d14] border border-white/10 rounded-2xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.6)]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/2">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <div className="w-3 h-3 rounded-full bg-green-500/60" />
                <div className="flex-1 mx-4 bg-white/5 rounded-md h-5" />
              </div>
              <div className="grid grid-cols-3 gap-px bg-white/5 border-b border-white/5">
                {[['42','Vendedores','#c9a84c'],['38','Compradores','#00d4c8'],['12','Cierres','#10b981']].map(([v,l,c]) => (
                  <div key={l} className="bg-[#0d0d14] px-4 py-4">
                    <div className="font-display font-black text-2xl" style={{ color: c }}>{v}</div>
                    <div className="text-[#7a7870] text-xs mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
              <div className="p-4 space-y-3">
                {[
                  { name: 'Marco Gómez', rate: 78, color: '#c9a84c' },
                  { name: 'Ana Vidal',   rate: 64, color: '#00d4c8' },
                  { name: 'Luis Torres', rate: 51, color: '#7c3aed' },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                      {a.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-white font-medium">{a.name}</span>
                        <span style={{ color: a.color }} className="font-bold">{a.rate}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${a.rate}%` }}
                          transition={{ duration: 1.2, delay: 0.5 + i * 0.2, ease: [0.25,0.46,0.45,0.94] }}
                          className="h-full rounded-full" style={{ background: a.color }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -inset-4 bg-[#c9a84c]/5 rounded-3xl blur-2xl -z-10" />
          </motion.div>
        </div>
      </Section>

      {/* ── PRICING ──────────────────────────────────────── */}
      <Section id="pricing" className="px-6 md:px-12 py-24">
        <motion.div variants={fadeUp} className="text-center mb-16 max-w-2xl mx-auto">
          <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.2em] mb-4">Precios</p>
          <h2 className="font-display font-black text-4xl md:text-5xl text-white leading-tight">
            Empieza gratis.<br />Escala cuando crezcas.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {PLANS.map((plan, i) => (
            <motion.div key={i} variants={fadeUp}>
              <SpotCard className={`relative rounded-2xl p-7 h-full flex flex-col ${
                plan.highlight
                  ? 'bg-gradient-to-b from-[#c9a84c]/10 to-transparent border-[#c9a84c]/40'
                  : 'bg-[#0d0d14] border-white/7'
              } border`}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c9a84c] text-black text-xs font-display font-black px-4 py-1 rounded-full">
                    MÁS POPULAR
                  </div>
                )}
                <div className="mb-6">
                  <p className="text-[#7a7870] text-sm mb-2">{plan.name}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-black text-4xl text-white">{plan.price}</span>
                    <span className="text-[#7a7870] text-sm">/{plan.sub}</span>
                  </div>
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-[#e8e6e0]">
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke={plan.highlight ? '#c9a84c' : '#ffffff30'} strokeWidth="1" />
                        <path d="M5 8L7 10L11 6" stroke={plan.highlight ? '#c9a84c' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => plan.href ? window.open(plan.href, '_blank', 'noopener,noreferrer') : navigate('/login')}
                  className={`w-full py-3 rounded-xl font-display font-bold text-sm transition-all duration-200 ${
                    plan.highlight
                      ? 'bg-[#c9a84c] hover:bg-[#e8c870] text-black hover:shadow-[0_0_30px_rgba(201,168,76,0.4)]'
                      : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  }`}>
                  {plan.cta}
                </button>
              </SpotCard>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── TESTIMONIALS ─────────────────────────────────── */}
      <Section id="testimonials" className="px-6 md:px-12 py-24 max-w-7xl mx-auto">
        <motion.div variants={fadeUp} className="text-center mb-16">
          <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.2em] mb-4">Testimonios</p>
          <h2 className="font-display font-black text-4xl text-white">Lo que dicen nuestros clientes</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={i} variants={fadeUp}>
              <SpotCard className="bg-[#0d0d14] border border-white/7 rounded-2xl p-6 h-full hover:border-white/15 transition-colors">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => <span key={j} className="text-[#c9a84c] text-sm">★</span>)}
                </div>
                <p className="text-[#e8e6e0] text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#c9a84c]/30 to-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center text-xs font-display font-black text-[#c9a84c]">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-[#7a7870] text-xs">{t.role}</p>
                  </div>
                </div>
              </SpotCard>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ── BUILT BY IACONSULTORA ───────────────────────── */}
      <Section className="px-6 md:px-12 py-20">
        <motion.div variants={fadeUp} className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d14] to-[#050508] p-10 md:p-14 flex flex-col md:flex-row items-center gap-10 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 50% 70% at 85% 50%, rgba(201,168,76,0.05) 0%, transparent 60%)' }} />
            <div className="flex-1 relative z-10">
              <p className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.2em] mb-4">Hecho por profesionales</p>
              <h2 className="font-display font-black text-3xl md:text-4xl text-white leading-tight mb-5">
                ¿Quieres crear<br />
                <span className="bg-gradient-to-r from-[#c9a84c] to-[#e8c870] bg-clip-text text-transparent">tu app como esta?</span>
              </h2>
              <p className="text-[#7a7870] text-sm leading-relaxed mb-7 max-w-md">
                E-HOME fue construida por <span className="text-white font-semibold">IaConsultora</span> — consultora especializada en desarrollo de software con IA. Desde idea hasta producto en producción.
              </p>
              <a href="https://www.iaconsultora.com" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#c9a84c] hover:bg-[#e8c870] text-black font-display font-black text-sm px-7 py-3.5 rounded-xl transition-all duration-200 hover:shadow-[0_0_30px_rgba(201,168,76,0.4)] hover:-translate-y-0.5">
                Conocer IaConsultora →
              </a>
            </div>
            <div className="flex-shrink-0 flex flex-col items-center gap-3 relative z-10">
              <div className="w-24 h-24 rounded-2xl border border-[#c9a84c]/20 bg-[#080810] flex items-center justify-center">
                <svg viewBox="0 0 48 48" width="44" height="44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="10" stroke="#c9a84c" strokeWidth="1.5" fill="none"/>
                  <circle cx="24" cy="24" r="3" fill="#c9a84c"/>
                  <line x1="24" y1="4" x2="24" y2="14" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="24" y1="34" x2="24" y2="44" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="4" y1="24" x2="14" y2="24" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="34" y1="24" x2="44" y2="24" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="10" y1="10" x2="17" y2="17" stroke="#c9a84c80" strokeWidth="1" strokeLinecap="round"/>
                  <line x1="31" y1="31" x2="38" y2="38" stroke="#c9a84c80" strokeWidth="1" strokeLinecap="round"/>
                  <line x1="38" y1="10" x2="31" y2="17" stroke="#c9a84c80" strokeWidth="1" strokeLinecap="round"/>
                  <line x1="17" y1="31" x2="10" y2="38" stroke="#c9a84c80" strokeWidth="1" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="font-display font-black text-xl text-white tracking-tight">Ia<span className="text-[#c9a84c]">Consultora</span></span>
              <span className="text-[#7a7870] text-xs">www.iaconsultora.com</span>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* ── CTA FINAL ────────────────────────────────────── */}
      <Section className="px-6 md:px-12 py-32 text-center relative">
        <div className="aurora-bg opacity-50">
          <div className="aurora-blob" />
          <div className="aurora-blob" />
        </div>
        <motion.div variants={fadeUp} className="relative z-10 max-w-3xl mx-auto">
          <h2 className="font-display font-black text-5xl md:text-6xl text-white leading-tight mb-6">
            Empieza hoy.<br />
            <span className="bg-gradient-to-r from-[#c9a84c] to-[#e8c870] bg-clip-text text-transparent">De forma totalmente gratuita.</span>
          </h2>
          <p className="text-[#7a7870] text-lg mb-10">
            Únete a cientos de agentes que ya gestionan su cartera de propiedades con E-<span className="text-[#c9a84c]">HOME</span>.
          </p>
          <MagneticBtn onClick={() => navigate('/login')}
            className="bg-[#c9a84c] hover:bg-[#e8c870] text-black font-display font-black text-lg px-10 py-5 rounded-xl transition-all duration-300 hover:shadow-[0_0_60px_rgba(201,168,76,0.5)] hover:-translate-y-1">
            Crear cuenta gratuita →
          </MagneticBtn>
        </motion.div>
      </Section>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <footer id="contact" className="border-t border-white/5 px-6 md:px-12 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#050508', border: '1.5px solid rgba(201,168,76,0.3)' }}>
              <svg viewBox="0 0 40 40" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="20,5 4,18 36,18" fill="#f0c040"/>
                <rect x="26" y="8" width="4" height="8" fill="#f0c040"/>
                <rect x="7" y="18" width="26" height="17" fill="#f0c040"/>
                <rect x="15" y="25" width="10" height="10" fill="#050508" rx="1"/>
                <circle cx="23" cy="30" r="1" fill="#c9a84c"/>
                <rect x="9" y="20" width="7" height="6" fill="#050508" rx="1"/>
                <rect x="24" y="20" width="7" height="6" fill="#050508" rx="1"/>
              </svg>
            </div>
            <span className="font-display font-bold text-white tracking-tight">E-<span className="text-[#c9a84c]">HOME</span></span>
          </div>
          <p className="text-[#7a7870] text-xs">© 2026 ehomesp.com · Todos los derechos reservados</p>
          <div className="flex gap-6 text-[#7a7870] text-xs">
            <Link to="/privacidad" className="hover:text-[#c9a84c] transition-colors">Privacidad</Link>
            <Link to="/cookies" className="hover:text-[#c9a84c] transition-colors">Cookies</Link>
            <Link to="/contacto" className="hover:text-[#c9a84c] transition-colors">Contacto</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
